import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FiBriefcase, FiCheck, FiX, FiRefreshCw, FiAlertCircle, FiLink2, FiArrowLeft } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useParams } from 'react-router-dom';
import { useClient } from '../contexts/ClientContext';
import { useLang } from '../contexts/LanguageContext';
import { getAuthMe } from '../api/clientsApi';
import { getTenants, switchTenant } from '../api/tenantApi';
import { apiClient } from '../api/apiClient';
import { queryClient } from '../lib/queryClient';
import { getLogo } from '../api/uploadsApi';

const baseUrl = process.env.REACT_APP_API_URL;

type Tenant = {
  tenantId: string;
  name: string;
  logo: string | null;
  status: 'active' | 'suspended';
  plan: string;
  role: string;
  lastUsedAt: string;
};

type TenantWithLogo = Tenant & {
  logoUrl: string | null;
  logoLoading: boolean;
};

interface SwitchAccountProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function SwitchAccount({ isOpen, onClose }: SwitchAccountProps) {
  const [tenants, setTenants] = useState<TenantWithLogo[]>([]);
  const [loading, setLoading] = useState(false);
  const [switching, setSwitching] = useState<string | null>(null);
  const [unlinking, setUnlinking] = useState<string | null>(null);
  const [previousCompany, setPreviousCompany] = useState<{ tenantId: string; name: string } | null>(null);
  const navigate = useNavigate();
  const { company } = useParams<{ company: string }>();
  const { setClient } = useClient();

  useEffect(() => {
    if (isOpen) {
      fetchTenants();
    }
  }, [isOpen]);

  // Load previous company after tenants are fetched
  // This shows the company you were in before (e.g., if you switched from "test" to "bajalza", "test" will show here)
  useEffect(() => {
    if (!isOpen) {
      setPreviousCompany(null);
      return;
    }

    // Load previous company from localStorage
    const prevTenantId = localStorage.getItem('previousTenantId');
    const prevTenantName = localStorage.getItem('previousTenantName');

    // Only show previous company if it's different from current company AND it's still linked
    if (prevTenantId && prevTenantName && prevTenantId !== company) {
      // Check if previous company is in the tenants list (must be linked to show)
      const prevTenant = tenants.find(t => t.tenantId === prevTenantId);
      if (prevTenant) {
        // Use tenant data from API for accuracy (name, status, etc.)
        setPreviousCompany({ tenantId: prevTenantId, name: prevTenant.name });
      } else {
        // Hide previous company if it's not linked anymore
        setPreviousCompany(null);
        // Clear from localStorage since it's no longer accessible
        localStorage.removeItem('previousTenantId');
        localStorage.removeItem('previousTenantName');
      }
    } else {
      setPreviousCompany(null);
    }
  }, [isOpen, tenants, company]);

  // Listen for tenant updates (when user is linked/unlinked)
  useEffect(() => {
    const handleTenantsUpdate = () => {
      if (isOpen) {
        fetchTenants();
      }
    };
    
    window.addEventListener('tenants-updated', handleTenantsUpdate);
    return () => {
      window.removeEventListener('tenants-updated', handleTenantsUpdate);
    };
  }, [isOpen]);

  async function fetchTenants() {
    try {
      setLoading(true);
      const data = await getTenants();
      const tenantsList = data.tenants || [];
      
      // Initialize tenants with logo loading state
      const tenantsWithLogos: TenantWithLogo[] = tenantsList.map((tenant: Tenant) => ({
        ...tenant,
        logoUrl: tenant.logo || null, // Use logo from API if available
        logoLoading: false,
      }));
      
      setTenants(tenantsWithLogos);
      
      // Fetch logos for each tenant in parallel
      // This ensures logos are displayed even if the backend doesn't return them in the tenant list
      Promise.all(
        tenantsWithLogos.map(async (tenant) => {
          try {
            const logoData = await getLogo(tenant.tenantId);
            if (logoData?.hasLogo && logoData?.url) {
              setTenants(prev => prev.map(t => 
                t.tenantId === tenant.tenantId 
                  ? { ...t, logoUrl: logoData.url!, logoLoading: false }
                  : t
              ));
            } else {
              // No logo found - keep existing logoUrl (from API) or null
              setTenants(prev => prev.map(t => 
                t.tenantId === tenant.tenantId 
                  ? { ...t, logoLoading: false }
                  : t
              ));
            }
          } catch (err) {
            // Silently fail - logo is optional
            console.warn(`[SwitchAccount] Failed to load logo for ${tenant.tenantId}:`, err);
            setTenants(prev => prev.map(t => 
              t.tenantId === tenant.tenantId 
                ? { ...t, logoLoading: false }
                : t
            ));
          }
        })
      );
      
      // If no tenants found, log for debugging
      if (tenantsList.length === 0) {
        console.warn('[SwitchAccount] No tenants found for user');
      }
    } catch (err: any) {
      console.error('Failed to fetch tenants:', err);
      toast.error('שגיאה בטעינת רשימת החברות');
      setTenants([]);
    } finally {
      setLoading(false);
    }
  }

  async function handleSwitch(tenantId: string) {
    // Only allow switching to companies that are in the tenants list (must be linked)
    const tenant = tenants.find(t => t.tenantId === tenantId);
    
    if (!tenant) {
      toast.error('חברה זו לא מקושרת לחשבון שלך. אנא פנה למנהל המערכת לקישור החברה.');
      return;
    }
    
    if (tenant.status === 'suspended') {
      toast.error('חברה זו מושעית ואינה נגישה כרגע');
      return;
    }

    // Save current company as previous before switching
    // This ensures when you switch from "test" to "bajalza", "test" is saved as previous
    if (company && company !== tenantId) {
      const currentTenant = tenants.find(t => t.tenantId === company);
      if (currentTenant) {
        localStorage.setItem('previousTenantId', company);
        localStorage.setItem('previousTenantName', currentTenant.name);
      } else {
        // If current company not in tenants list, try to get name from localStorage or use company slug
        const currentName = localStorage.getItem('activeTenantName') || company;
        localStorage.setItem('previousTenantId', company);
        localStorage.setItem('previousTenantName', currentName);
      }
    } else {
    }

    try {
      setSwitching(tenantId);
      
      // 1) Clear all React Query cache to prevent data leakage between companies
      queryClient.clear();
      
      const data = await switchTenant(tenantId);

      // 2) Update token in localStorage
      // PHASE 4: Auth cookies must be created ONLY by the backend via setAuthCookie().
      // This JavaScript cookie conflicts with the httpOnly auth cookie and can create two different `token` cookies.
      // After switch, the backend will set the auth cookie via setAuthCookie() in the response.
      if (data.token) {
        localStorage.setItem('token', data.token);
        localStorage.setItem('accessToken', data.token);
        // Do NOT set cookie here - backend handles it via setAuthCookie()
      }

      // 3) Update active tenant and company slug in localStorage BEFORE navigation
      localStorage.setItem('activeTenantId', tenantId);
      localStorage.setItem('activeTenantName', tenant?.name || data.tenant?.name || tenantId);
      localStorage.setItem('activeRole', data.tenant?.role || tenant?.role || data.user?.role || 'admin');
      // Use new localStorage key (v2) - URL is always source of truth
      localStorage.setItem('wjs_active_company_v2', tenantId);
      // Keep old key for backward compatibility during transition
      localStorage.setItem('currentCompany', tenantId);

      // 4) Update axios default headers with new token for all future requests
      if (data.token) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;
        // Update the api instance from ClientContext as well
        const apiInstance = (window as any).__apiInstance;
        if (apiInstance) {
          apiInstance.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;
        }
      }

      // 5) Refresh client context with new company
      if (setClient) {
        try {
          const me = await getAuthMe(tenantId);
          if (me && typeof me === 'object' && '_id' in me) {
            setClient({
              ...me,
              _id: me._id as string,
              role: data.tenant?.role || data.user?.role || tenant?.role || me.role || 'admin',
              activeTenantId: tenantId,
            });
          }
        } catch (err) {
          console.error('Failed to fetch client data:', err);
          // Set client data from response if available
          if (data.user) {
            setClient({
              _id: data.user.id,
              role: (data.user.role || data.tenant?.role || 'admin') as 'client' | 'admin' | 'manager' | 'agent' | 'superadmin',
              activeTenantId: tenantId,
              email: data.user.email,
              fullName: data.user.fullName,
              phone: data.user.phone || '',
              totalSessions: data.user.totalSessions || 0,
              usedSessions: data.user.usedSessions || 0,
            });
          }
        }
      }

      toast.success(`עברת לחברה: ${tenant?.name || data.tenant?.name || tenantId}`);
      
      // Close modal first
      onClose();
      
      // 6) Navigate to new tenant - use full reload to ensure clean state
      // Small delay to ensure all state updates complete
      setTimeout(() => {
        window.location.href = `/${tenantId}/home`;
      }, 200);
    } catch (err: any) {
      console.error('Failed to switch tenant:', err);
      const errorMsg = err?.response?.data?.error || 'שגיאה בהחלפת חברה';
      const errorCode = err?.response?.data?.code;
      
      // If company is not linked, show specific message and clear previous company if needed
      if (errorCode === 'TENANT_NOT_LINKED' || errorMsg.includes('not linked')) {
        toast.error('חברה זו לא מקושרת לחשבון שלך. אנא פנה למנהל המערכת לקישור החברה.');
        // Clear previous company if it was the one we tried to switch to
        if (previousCompany?.tenantId === tenantId) {
          setPreviousCompany(null);
          localStorage.removeItem('previousTenantId');
          localStorage.removeItem('previousTenantName');
        }
      } else {
        toast.error(errorMsg);
      }
      setSwitching(null);
    }
  }

  async function handleUnlink(tenantId: string, e: React.MouseEvent) {
    e.stopPropagation(); // Prevent triggering switch
    
    // Ask if user wants to unlink both directions or just one
    const unlinkBidirectional = window.confirm(
      `האם אתה רוצה לנתק את הקישור בשני הכיוונים?\n\n` +
      `לחיצה על "אישור" = ניתוק דו-כיווני (תנתק גם את הקישור מהחברה השנייה אליך)\n` +
      `לחיצה על "ביטול" = ניתוק חד-כיווני (רק הקישור שלך אל החברה השנייה)`
    );
    
    // Confirm the unlink action
    const confirmMessage = unlinkBidirectional
      ? `האם אתה בטוח שברצונך לנתק את הקישור בשני הכיוונים?\nזה יסיר את הקישור גם מהחברה השנייה אליך.`
      : `האם אתה בטוח שברצונך לנתק את הקישור לחברה זו?`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setUnlinking(tenantId);
      
      // Get current user ID from auth context or localStorage
      const me = await getAuthMe(company!);
      const userId = (me && typeof me === 'object' && '_id' in me) ? (me._id as string) : undefined;
      const userEmail = (me && typeof me === 'object' && 'email' in me) ? (me.email as string) : undefined;
      
      if (!userId) {
        toast.error('לא ניתן לזהות את המשתמש');
        return;
      }

      const rpcTenants = (await import('../api/rpc/rpcTenants')).default;
      const { data } = await rpcTenants.unlinkTenant({
        userId,
        tenantId,
        sourceCompanySlug: company, // Send source company to help find the user
        email: userEmail, // Send email as fallback
        bidirectional: unlinkBidirectional, // Tell backend if we want bidirectional unlink
      });
      if (!data?.ok) {
        throw new Error(data?.error || 'Failed to unlink tenant');
      }

      toast.success(
        unlinkBidirectional 
          ? 'קישורים בוטלו בהצלחה בשני הכיוונים'
          : 'קישור לחברה בוטל בהצלחה'
      );
      
      // Check if user is currently logged into the unlinked company
      // If so, redirect them to their main company (source company)
      const isCurrentlyInUnlinkedCompany = company === tenantId;
      
      // Immediately remove from local state for instant UI update
      setTenants(prev => prev.filter(t => t.tenantId !== tenantId));
      
      // Clear previous company if it was the unlinked one
      if (previousCompany?.tenantId === tenantId) {
        setPreviousCompany(null);
        localStorage.removeItem('previousTenantId');
        localStorage.removeItem('previousTenantName');
      }
      
      // Also check if previous company is still in tenants list after refresh, if not, clear it
      const updatedTenants = tenants.filter(t => t.tenantId !== tenantId);
      if (previousCompany && !updatedTenants.find(t => t.tenantId === previousCompany.tenantId)) {
        setPreviousCompany(null);
        localStorage.removeItem('previousTenantId');
        localStorage.removeItem('previousTenantName');
      }
      
      // Refresh tenants list from server to ensure consistency
      await fetchTenants();
      
      // If user is currently in the unlinked company, redirect to their main company
      if (isCurrentlyInUnlinkedCompany) {
        // Get updated tenants list after unlink
        const updatedTenantsList = tenants.filter(t => t.tenantId !== tenantId);
        
        // Find the user's main company (source company) from the remaining tenants
        // The source company is the one where the user's account exists (where we sent the unlink request from)
        const mainCompany = company || (updatedTenantsList.length > 0 ? updatedTenantsList[0].tenantId : null);
        
        if (mainCompany) {
          toast.warning('החברה נותקה. מעבר לחברה הראשית שלך...');
          
          // Close modal first
          onClose();
          
          // Redirect to main company after a short delay
          setTimeout(() => {
            window.location.href = `/${mainCompany}/home`;
          }, 1000);
        } else {
          toast.warning('החברה נותקה. אנא בחר חברה אחרת או פנה למנהל המערכת.');
        }
      }
    } catch (err: any) {
      console.error('Failed to unlink tenant:', err);
      const errorMsg = err?.response?.data?.error || 'שגיאה בניתוק קישור לחברה';
      toast.error(errorMsg);
    } finally {
      setUnlinking(null);
    }
  }

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[1000] bg-black/40 grid place-items-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="
            bg-white/95 backdrop-blur-2xl rounded-3xl shadow-2xl
            w-[95vw] max-w-md max-h-[90vh] overflow-y-auto relative
          "
          initial={{ y: 20, opacity: 0, scale: 0.95 }}
          animate={{ y: 0, opacity: 1, scale: 1 }}
          exit={{ y: 10, opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.2 }}
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="sticky top-0 bg-white/95 backdrop-blur-xl border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-3xl" dir="rtl">
            <h2 className="text-xl font-bold text-gray-900">החלף חברה</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <FiX className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <FiRefreshCw className="w-6 h-6 animate-spin text-blue-600" />
                <span className="mr-3 text-gray-600">טוען...</span>
              </div>
            ) : tenants.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <FiBriefcase className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                <p>לא נמצאו חברות מקושרות</p>
              </div>
            ) : (
              <div className="space-y-2">
                {/* Previous Company - Quick Return (only shown if still linked) */}
                {previousCompany && (() => {
                  const prevTenant = tenants.find(t => t.tenantId === previousCompany.tenantId);
                  const isPrevSuspended = prevTenant?.status === 'suspended';
                  const isPrevInList = !!prevTenant;
                  // Only allow switching if company is in tenants list and not suspended
                  const isPrevAccessible = isPrevInList && !isPrevSuspended;
                  
                  // Don't show if not linked
                  if (!isPrevInList) {
                    return null;
                  }
                  
                  return (
                    <motion.button
                      onClick={() => {
                        if (isPrevAccessible) {
                          handleSwitch(previousCompany.tenantId);
                        } else {
                          toast.error('חברה זו מושעית ואינה נגישה');
                        }
                      }}
                      disabled={switching === previousCompany.tenantId || !isPrevAccessible}
                      className={`
                        w-full p-4 rounded-xl border-2 transition-all
                        flex items-center gap-4 text-right
                        border-purple-300 bg-gradient-to-r from-purple-50 to-blue-50 
                        hover:border-purple-400 hover:from-purple-100 hover:to-blue-100
                        ${switching === previousCompany.tenantId ? 'opacity-60 cursor-wait' : 'cursor-pointer'}
                      `}
                      whileHover={switching !== previousCompany.tenantId && isPrevAccessible ? { scale: 1.02 } : {}}
                      whileTap={switching !== previousCompany.tenantId && isPrevAccessible ? { scale: 0.98 } : {}}
                    >
                      {/* Logo or Arrow Icon */}
                      <div className="flex-shrink-0">
                        {prevTenant?.logoUrl ? (
                          <img
                            src={prevTenant.logoUrl}
                            alt={previousCompany.name}
                            className="w-12 h-12 rounded-lg object-cover border border-gray-200"
                            onError={(e) => {
                              // Fallback to arrow icon if image fails to load
                              const target = e.target as HTMLImageElement;
                              target.style.display = 'none';
                              const placeholder = target.nextElementSibling as HTMLElement;
                              if (placeholder) {
                                placeholder.style.display = 'flex';
                              }
                            }}
                          />
                        ) : null}
                        <div className={`w-12 h-12 rounded-lg bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center ${prevTenant?.logoUrl ? 'hidden' : ''}`}>
                          <FiArrowLeft className="w-6 h-6 text-white" />
                        </div>
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <h3 className="font-bold text-gray-900 truncate">{previousCompany.name}</h3>
                            <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-purple-100 text-purple-700">
                              חברה קודמת
                            </span>
                          </div>
                          {switching === previousCompany.tenantId ? (
                            <FiRefreshCw className="w-5 h-5 animate-spin text-purple-600 flex-shrink-0" />
                          ) : null}
                        </div>
                        <p className="text-sm text-gray-600">
                          לחץ כדי לחזור לחברה הקודמת
                        </p>
                      </div>
                    </motion.button>
                  );
                })()}

                {/* Divider if previous company exists */}
                {previousCompany && (
                  <div className="flex items-center gap-3 my-3">
                    <div className="flex-1 h-px bg-gray-200"></div>
                    <span className="text-xs text-gray-500 font-medium">כל החברות</span>
                    <div className="flex-1 h-px bg-gray-200"></div>
                  </div>
                )}

                {tenants.map((tenant) => {
                  const isActive = company === tenant.tenantId;
                  const isSuspended = tenant.status === 'suspended';
                  const isSwitching = switching === tenant.tenantId;

                  return (
                    <motion.button
                      key={tenant.tenantId}
                      onClick={() => !isSuspended && !isSwitching && handleSwitch(tenant.tenantId)}
                      disabled={isSuspended || isSwitching}
                      className={`
                        w-full p-4 rounded-xl border-2 transition-all
                        flex items-center gap-4 text-right
                        ${isActive
                          ? 'border-blue-500 bg-blue-50'
                          : isSuspended
                          ? 'border-red-200 bg-red-50 opacity-60 cursor-not-allowed'
                          : 'border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50'
                        }
                        ${isSwitching ? 'opacity-60 cursor-wait' : ''}
                      `}
                      whileHover={!isSuspended && !isSwitching ? { scale: 1.02 } : {}}
                      whileTap={!isSuspended && !isSwitching ? { scale: 0.98 } : {}}
                    >
                      {/* Logo */}
                      <div className="flex-shrink-0">
                        {tenant.logoUrl ? (
                          <img
                            src={tenant.logoUrl}
                            alt={tenant.name}
                            className="w-12 h-12 rounded-lg object-cover border border-gray-200"
                            onError={(e) => {
                              // Fallback to placeholder if image fails to load
                              const target = e.target as HTMLImageElement;
                              target.style.display = 'none';
                              const placeholder = target.nextElementSibling as HTMLElement;
                              if (placeholder) {
                                placeholder.style.display = 'flex';
                              }
                            }}
                          />
                        ) : null}
                        <div 
                          className={`w-12 h-12 rounded-lg bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center ${tenant.logoUrl ? 'hidden' : ''}`}
                        >
                          <FiBriefcase className="w-6 h-6 text-white" />
                        </div>
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <h3 className="font-bold text-gray-900 truncate">{tenant.name}</h3>
                          <div className="flex items-center gap-2">
                            {isActive && (
                              <FiCheck className="w-5 h-5 text-blue-600 flex-shrink-0" />
                            )}
                            {/* Unlink button - only show if not active company */}
                            {!isActive && (
                              <div
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleUnlink(tenant.tenantId, e as any);
                                }}
                                className="p-1.5 rounded-lg hover:bg-red-50 text-red-600 hover:text-red-700 transition-colors cursor-pointer"
                                title="נתק קישור לחברה זו"
                              >
                                {unlinking === tenant.tenantId ? (
                                  <FiRefreshCw className="w-4 h-4 animate-spin" />
                                ) : (
                                  <FiLink2 className="w-4 h-4 rotate-45" />
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                            isSuspended
                              ? 'bg-red-100 text-red-700'
                              : 'bg-green-100 text-green-700'
                          }`}>
                            {isSuspended ? 'מושעית' : 'פעילה'}
                          </span>
                          <span className="text-gray-500">•</span>
                          <span className="text-gray-600 capitalize">{tenant.role}</span>
                          {tenant.plan && (
                            <>
                              <span className="text-gray-500">•</span>
                              <span className="text-gray-600">{tenant.plan}</span>
                            </>
                          )}
                        </div>
                      </div>

                      {/* Loading indicator */}
                      {isSwitching && (
                        <FiRefreshCw className="w-5 h-5 animate-spin text-blue-600 flex-shrink-0" />
                      )}

                      {/* Suspended warning */}
                      {isSuspended && (
                        <div className="flex-shrink-0">
                          <FiAlertCircle className="w-5 h-5 text-red-500" />
                        </div>
                      )}
                    </motion.button>
                  );
                })}
              </div>
            )}

            {/* Suspended notice */}
            {tenants.some(t => t.status === 'suspended') && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                <FiAlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-800">
                  חברות מושעיות מופיעות ברשימה אך לא ניתן לגשת אליהן.
                </p>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

